/**
 * Types for the email panel part of the reply-to-AI-emails contract (ZOO-696 <->
 * ZOO-698, v0.3, section 1). The chain-of-custody entry shape lives next to
 * AlertCompletion in ./types (CocEmail).
 *
 * Every key here is additive and appears only when the backend gate is on.
 * `native` being present is the only switch for the new UI.
 */

export interface EmailReplyTargetThread {
  id: string;
  /** A = AI agent thread, B = courier mail to the support inbox */
  case: 'A' | 'B';
  paused: boolean;
  paused_by: { name: string } | null;
  paused_at: string | null;
  can_resume_ai: boolean;
  /** Send as last_seen_message_id when replying. */
  last_message_id: string | null;
  last_human_reply: { author_name: string; at: string } | null;
}

export interface EmailDraftUser {
  body: string;
  attachment_ids: string[];
  updated_at: string;
}

export type EmailDraftAIStatus = 'none' | 'pending' | 'ready' | 'failed';

export interface EmailDraftAI {
  status: EmailDraftAIStatus;
  body: string | null;
  generated_at: string | null;
}

export interface EmailPanelNative {
  contract_version: string;
  reply_target_thread: EmailReplyTargetThread | null;
  threads_count: number;
  replyable: boolean;
  not_replyable_reason: string | null;
  can_reply: boolean;
  needs_reply: boolean;
  unread: boolean;
  /** null when can_reply is false: no draft of any kind is returned. */
  draft: { user: EmailDraftUser | null; ai: EmailDraftAI } | null;
}

export interface EmailTranscriptItemMeta {
  message_id: string;
  thread_id: string;
  direction: 'inbound' | 'outbound';
  author: {
    type: 'AI' | 'USER' | 'EXTERNAL';
    name: string;
    account_email: string | null;
    /** The shipment's courier, on a received (EXTERNAL) email; null when unknown. */
    courier?: string | null;
  };
  /** The message's own subject; null when it has none. */
  subject?: string | null;
  to: string[];
  cc: string[];
  summary: string | null;
  needs_reply: boolean;
  attachment_names: string[];
  status: string | null;
}

/** An unknown draft status from a newer backend is treated as `none`. */
export function normalizeDraftAIStatus(status: unknown): EmailDraftAIStatus {
  return status === 'pending' || status === 'ready' || status === 'failed'
    ? status
    : 'none';
}
